import React, { useEffect, useState, useMemo } from 'react'
import { connect } from 'react-redux'
import { useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  getQuotationById,
  setQuotationToEdit,
} from '../../../store/actions'
import BotoneraModern from '../../Botonera/BotoneraModern.tsx'
import {
  Building2,
  Calendar,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Download,
  MapPin,
  FileText,
} from 'lucide-react'
import RegisteredUserProfileLoader from '../../RegisteredUsers/RegisteredUsersSkeleton/RegisteredUserProfileLoader'
import InfoItem from './InfoItem.tsx'
import styles from './PublicQuotationByIdV2.module.scss'
import useNavVisibility from 'hooks/useNavVisibility.ts'

interface DescriptionLine {
  key: string
  value: string
}

interface ProcessedDescription {
  isText: boolean
  text?: string
  lines?: DescriptionLine[]
}

interface PublicQuotationByIdV2Props {
  publicQuotation: any
  getQuotationById: (id: string, edit?: boolean, publicView?: boolean) => void
  userData: any
}

const PublicQuotationByIdV2: React.FC<PublicQuotationByIdV2Props> = ({
  publicQuotation,
  getQuotationById,
  userData,
}) => {
  const { ocultarEnMobile } = useNavVisibility()
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const { id } = useParams<{ id: string }>()

  const processDescription = (desc: string | undefined): ProcessedDescription => {
    if (!desc) return { isText: false, lines: [] }

    const trimmedDesc = desc.trim()
    if (!trimmedDesc) return { isText: false, lines: [] }

    const rawLines = trimmedDesc.split('\r\n')
    const nonEmptyLines = rawLines.filter((line) => line.trim() !== '')

    const isValidKey = (key: string): boolean => {
      const trimmedKey = key.trim()
      if (!trimmedKey || trimmedKey.length > 50) return false
      
      const wordCount = trimmedKey.split(/\s+/).length
      if (wordCount > 6) return false
      
      const hasValidPattern = /^[A-ZÁÉÍÓÚÑa-záéíóúñ0-9\s\-()/]+$/.test(trimmedKey)
      const startsWithCapital = /^[A-ZÁÉÍÓÚÑ]/.test(trimmedKey)
      
      return hasValidPattern && (startsWithCapital || wordCount <= 3)
    }

    const allText = nonEmptyLines.join(' ')
    const colonMatches = allText.match(/[^:]+:/g)
    
    if (colonMatches && colonMatches.length >= 2) {
      const result: DescriptionLine[] = []
      let validPairs = 0
      let lastIndex = 0
      
      for (let i = 0; i < colonMatches.length; i++) {
        const keyMatch = colonMatches[i]
        const key = keyMatch.replace(/:$/, '').trim()
        
        if (!isValidKey(key)) continue
        
        const keyStart = allText.indexOf(keyMatch, lastIndex)
        const keyEnd = keyStart + keyMatch.length
        const valueStart = keyEnd
        
        let valueEnd = allText.length
        if (i < colonMatches.length - 1) {
          const nextKeyMatch = colonMatches[i + 1]
          const nextKeyStart = allText.indexOf(nextKeyMatch, keyEnd)
          valueEnd = nextKeyStart
        }
        
        const value = allText.substring(valueStart, valueEnd).trim()
        
        if (key && value && value.length > 0 && value.length < 500) {
          result.push({ key, value })
          validPairs++
        }
        
        lastIndex = keyEnd
      }
      
      if (validPairs >= 3 || (validPairs >= 2 && result.every(item => item.value.length < 100))) {
        return { isText: false, lines: result }
      }
    }
    
    if (nonEmptyLines.length <= 1) {
      return { isText: true, text: trimmedDesc }
    }

    const result: DescriptionLine[] = []
    let currentKey: string | null = null
    let currentValue: string[] = []
    let validPairsCount = 0

    for (let i = 0; i < rawLines.length; i++) {
      const line = rawLines[i].trim()

      if (!line) {
        if (currentKey && currentValue.length > 0) {
          const value = currentValue.join(' ').trim()
          if (isValidKey(currentKey) && value.length > 0 && value.length < 500) {
            result.push({
              key: currentKey,
              value,
            })
            validPairsCount++
          }
          currentKey = null
          currentValue = []
        }
        continue
      }

      const colonIndex = line.indexOf(':')
      if (colonIndex > 0 && colonIndex < line.length - 1) {
        const key = line.substring(0, colonIndex).trim()
        const value = line.substring(colonIndex + 1).trim()
        
        if (currentKey && currentValue.length > 0) {
          const prevValue = currentValue.join(' ').trim()
          if (isValidKey(currentKey) && prevValue.length > 0 && prevValue.length < 500) {
            result.push({
              key: currentKey,
              value: prevValue,
            })
            validPairsCount++
          }
        }
        
        if (key && value && isValidKey(key)) {
          currentKey = key
          currentValue = [value]
        } else if (key && isValidKey(key)) {
          currentKey = key
          currentValue = []
        } else if (currentKey) {
          currentValue.push(line)
        }
        continue
      }

      const nextLine = i < rawLines.length - 1 ? rawLines[i + 1]?.trim() : ''
      const isLikelyKey =
        line.endsWith(':') ||
        (nextLine !== '' &&
          !nextLine.endsWith(':') &&
          !nextLine.includes(':') &&
          line.length < 50 &&
          isValidKey(line))

      if (isLikelyKey && !currentKey) {
        if (currentValue.length > 0 && result.length > 0) {
          const lastItem = result[result.length - 1]
          lastItem.value = (lastItem.value + ' ' + currentValue.join(' ')).trim()
          currentValue = []
        }
        currentKey = line.replace(/:$/, '').trim()
      } else if (currentKey) {
        currentValue.push(line)
      } else if (result.length === 0 && i === 0) {
        return { isText: true, text: trimmedDesc }
      }
    }

    if (currentKey && currentValue.length > 0) {
      const value = currentValue.join(' ').trim()
      if (isValidKey(currentKey) && value.length > 0 && value.length < 500) {
        result.push({
          key: currentKey,
          value,
        })
        validPairsCount++
      }
    }

    if (result.length === 0 || validPairsCount < 2) {
      return { isText: true, text: trimmedDesc }
    }

    return { isText: false, lines: result }
  }

  const processedDescription = useMemo(
    () => processDescription(publicQuotation?.description),
    [publicQuotation?.description]
  )

  const visibleLines = useMemo(() => {
    if (processedDescription.isText) return []
    return isDescriptionExpanded
      ? processedDescription.lines || []
      : (processedDescription.lines || []).slice(0, 5)
  }, [processedDescription, isDescriptionExpanded])

  useEffect(() => {
    getQuotationById(id || '', false, true)
  }, [])

  useEffect(() => {
    if (!publicQuotation || publicQuotation?.id !== id) {
      setIsLoading(true)
      getQuotationById(id || '', false, true)
    } else {
      setIsLoading(false)
    }
  }, [publicQuotation, id, getQuotationById])

  const formatDate = (date: string | undefined): string => {
    if (!date) return 'No informado'
    const utcDate = new Date(date)
    const year = utcDate.getUTCFullYear()
    const month = String(utcDate.getUTCMonth() + 1).padStart(2, '0')
    const day = String(utcDate.getUTCDate()).padStart(2, '0')
    return `${day}/${month}/${year}`
  }

  const formatMoney = (amount: number | undefined): string => {
    if (!amount) return 'No informado'
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loaderContainer}>
          <RegisteredUserProfileLoader smallVersion={false} noBackground={false} />
        </div>
      </div>
    )
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        {ocultarEnMobile && <BotoneraModern mobileStylesFromParent={ocultarEnMobile} />}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className={styles.content}
        >
          <div className={styles.header}>
            <p className={styles.subtitle}>Materiales a licitar Públicos</p>
            <h1 className={styles.title}>{publicQuotation?.name || 'Sin título'}</h1>
          </div>

          <div className={styles.layout}>
            <div className={styles.mainContent}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className={styles.card}
              >
                <h2 className={styles.cardTitle}>Información General</h2>
                <div className={styles.infoList}>
                  <InfoItem
                    icon={MapPin}
                    title="Ubicación"
                    content={publicQuotation?.jurisdiccion?.name || 'No informado'}
                  />
                  <InfoItem
                    icon={Calendar}
                    title="Fecha de apertura"
                    content={formatDate(publicQuotation?.startDate)}
                  />
                  <InfoItem
                    icon={DollarSign}
                    title="Moneda"
                    content={
                      publicQuotation?.currency && publicQuotation.currency !== 'undefined'
                        ? publicQuotation.currency
                        : 'Pesos Arg'
                    }
                  />
                  <InfoItem
                    icon={DollarSign}
                    title="Presupuesto oficial"
                    content={formatMoney(publicQuotation?.monto)}
                  />
                </div>

                <div className={styles.descriptionSection}>
                  <div className={styles.descriptionHeader}>
                    <FileText className={styles.descriptionIcon} size={20} />
                    <h3 className={styles.descriptionTitle}>Descripción</h3>
                  </div>
                  <div className={styles.descriptionContent}>
                    {processedDescription.isText ? (
                      <p className={styles.descriptionText}>
                        {processedDescription.text || 'No hay descripción disponible'}
                      </p>
                    ) : (
                      <div className={styles.descriptionTable}>
                        {visibleLines.map((line, index) => (
                          <div key={index} className={styles.descriptionRow}>
                            <span className={styles.descriptionKey}>{line.key}:</span>
                            <span className={styles.descriptionValue}>{line.value}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {!processedDescription.isText &&
                    (processedDescription.lines?.length || 0) > 5 && (
                      <button
                        className={styles.expandButton}
                        onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                      >
                        {isDescriptionExpanded ? 'Ver menos' : 'Ver más'}
                        {isDescriptionExpanded ? (
                          <ChevronUp className={styles.buttonIcon} size={16} />
                        ) : (
                          <ChevronDown className={styles.buttonIcon} size={16} />
                        )}
                      </button>
                    )}
                </div>
              </motion.div>
            </div>

            <div className={styles.sidebar}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
                className={styles.card}
              >
                <h2 className={styles.cardTitle}>Comitente</h2>
                <div className={styles.sidebarItem}>
                  <Building2 className={styles.sidebarIcon} size={20} />
                  <p className={styles.sidebarText}>
                    {publicQuotation?.comitente || 'No informado'}
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.3 }}
                className={styles.card}
              >
                <h2 className={styles.cardTitle}>Tipo de Obra</h2>
                <p className={styles.sidebarText}>
                  {publicQuotation?.obra_type?.name || 'No informado'}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.4 }}
                className={styles.card}
              >
                <h2 className={styles.cardTitle}>Organismo</h2>
                <p className={styles.sidebarText}>
                  {publicQuotation?.organismo?.name || 'No informado'}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.5 }}
                className={styles.card}
              >
                <h2 className={styles.cardTitle}>Documentación</h2>
                {publicQuotation?.link ? (
                  <a
                    href={publicQuotation.link}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.downloadButton}
                  >
                    <Download className={styles.downloadIcon} size={18} />
                    <span>Descargar pliego</span>
                  </a>
                ) : (
                  <p className={styles.noDocumentText}>No hay documentación disponible</p>
                )}
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

const mapStateToProps = (state: any) => {
  return {
    userData: state.general.userData,
    publicQuotation: state.profile.publicQuotation.quotation,
  }
}

export default connect(mapStateToProps, {
  getQuotationById,
  setQuotationToEdit,
})(PublicQuotationByIdV2)
